import { useState, useCallback } from 'react'
import type { ChipValue } from '@/types'
import type { RuletaBet, RuletaBetType } from '../utils/ruletaLogic'
import {
  getColumnNumbers,
  getDozenNumbers,
  getOutsideBetNumbers,
  getStreetNumbers,
  getLineNumbers,
  getCornerNumbers,
  getSplitNumbers,
} from '../utils/ruletaLogic'
import { useAuth } from '@/features/auth/contexts/AuthContext'

interface UseRuletaBetsReturn {
  bets: RuletaBet[]
  selectedChip: ChipValue
  totalBet: number
  selectChip: (value: ChipValue) => void
  placeBet: (type: RuletaBetType, numbers: number[], position?: string) => void
  placeStraightBet: (number: number) => void
  placeSplitBet: (num1: number, num2: number) => void
  placeStreetBet: (row: number) => void
  placeCornerBet: (topLeft: number) => void
  placeLineBet: (row: number) => void
  placeColumnBet: (column: 1 | 2 | 3) => void
  placeDozenBet: (dozen: 1 | 2 | 3) => void
  placeOutsideBet: (type: 'red' | 'black' | 'even' | 'odd' | 'low' | 'high') => void
  clearBets: () => void
  repeatLastBets: (lastBets: RuletaBet[]) => void
  doubleBets: () => void
  canPlaceBet: (amount: number) => boolean
}

export function useRuletaBets(): UseRuletaBetsReturn {
  const { user } = useAuth()
  const [bets, setBets] = useState<RuletaBet[]>([])
  const [selectedChip, setSelectedChip] = useState<ChipValue>(100)

  const totalBet = bets.reduce((sum, bet) => sum + bet.amount, 0)

  const canPlaceBet = useCallback(
    (amount: number) => {
      if (!user) return false
      return totalBet + amount <= user.balance
    },
    [user, totalBet]
  )

  const selectChip = useCallback((value: ChipValue) => {
    setSelectedChip(value)
  }, [])

  const placeBet = useCallback(
    (type: RuletaBetType, numbers: number[], position?: string) => {
      if (!canPlaceBet(selectedChip)) return

      setBets((prev) => {
        // Check if bet already exists on same position
        const existingIndex = prev.findIndex(
          (b) =>
            b.type === type &&
            b.numbers.length === numbers.length &&
            b.numbers.every((n, i) => n === numbers[i])
        )

        if (existingIndex >= 0) {
          // Add to existing bet
          const updated = [...prev]
          updated[existingIndex] = {
            ...updated[existingIndex],
            amount: updated[existingIndex].amount + selectedChip,
          }
          return updated
        }

        // Add new bet
        return [
          ...prev,
          {
            type,
            numbers,
            amount: selectedChip,
            position,
          },
        ]
      })
    },
    [selectedChip, canPlaceBet]
  )

  const placeStraightBet = useCallback(
    (number: number) => {
      placeBet('straight', [number], `straight-${number}`)
    },
    [placeBet]
  )

  const placeSplitBet = useCallback(
    (num1: number, num2: number) => {
      const numbers = getSplitNumbers(num1, num2)
      placeBet('split', numbers, `split-${numbers[0]}-${numbers[1]}`)
    },
    [placeBet]
  )

  const placeStreetBet = useCallback(
    (row: number) => {
      placeBet('street', getStreetNumbers(row), `street-${row}`)
    },
    [placeBet]
  )

  const placeCornerBet = useCallback(
    (topLeft: number) => {
      placeBet('corner', getCornerNumbers(topLeft), `corner-${topLeft}`)
    },
    [placeBet]
  )

  const placeLineBet = useCallback(
    (row: number) => {
      placeBet('line', getLineNumbers(row), `line-${row}`)
    },
    [placeBet]
  )

  const placeColumnBet = useCallback(
    (column: 1 | 2 | 3) => {
      placeBet('column', getColumnNumbers(column), `column-${column}`)
    },
    [placeBet]
  )

  const placeDozenBet = useCallback(
    (dozen: 1 | 2 | 3) => {
      placeBet('dozen', getDozenNumbers(dozen), `dozen-${dozen}`)
    },
    [placeBet]
  )

  const placeOutsideBet = useCallback(
    (type: 'red' | 'black' | 'even' | 'odd' | 'low' | 'high') => {
      placeBet(type, getOutsideBetNumbers(type), type)
    },
    [placeBet]
  )

  const clearBets = useCallback(() => {
    setBets([])
  }, [])

  const repeatLastBets = useCallback(
    (lastBets: RuletaBet[]) => {
      const totalLastBet = lastBets.reduce((sum, b) => sum + b.amount, 0)
      if (!user || totalLastBet > user.balance) return
      setBets(lastBets)
    },
    [user]
  )

  const doubleBets = useCallback(() => {
    if (!user) return
    const doubledTotal = totalBet * 2
    if (doubledTotal > user.balance) return

    setBets((prev) =>
      prev.map((bet) => ({
        ...bet,
        amount: bet.amount * 2,
      }))
    )
  }, [user, totalBet])

  return {
    bets,
    selectedChip,
    totalBet,
    selectChip,
    placeBet,
    placeStraightBet,
    placeSplitBet,
    placeStreetBet,
    placeCornerBet,
    placeLineBet,
    placeColumnBet,
    placeDozenBet,
    placeOutsideBet,
    clearBets,
    repeatLastBets,
    doubleBets,
    canPlaceBet,
  }
}
